import { bodyHealthId, bodyHealthTimestampCompare, validBodyHealthTimestamp } from '../../../supabase/functions/_shared/bodyHealthActionDomain';
import type { HealthProvider, WorkoutRow } from './healthMap';

export interface HealthWorkoutImportResult { written: number; preserved_conflicts: number }
export interface HealthWorkoutImportQuery extends PromiseLike<unknown> {
  abortSignal(signal: AbortSignal): HealthWorkoutImportQuery;
  setHeader(name: string, value: string): HealthWorkoutImportQuery;
}
export interface HealthWorkoutImportClient { rpc: unknown }
interface HealthWorkoutImportRpcClient {
  rpc(name: 'import_native_health_workouts_v1', args: { p_connection_id: string; p_provider: HealthProvider; p_records: WorkoutRow[]; p_recent_run_id?: string }): HealthWorkoutImportQuery;
}
export interface HealthWorkoutImportOptions {
  client: HealthWorkoutImportClient; rows: readonly WorkoutRow[];
  ownerId: string; connectionId: string; provider: HealthProvider; bearer: string;
  signal: AbortSignal; assertCurrent: () => void; recentRunId?: string;
  onConfirmed?: (delta: HealthWorkoutImportResult) => void;
  // A recent sync shares this set across its bounded requests. An ID already
  // confirmed for another origin cannot be attributed to a later batch.
  confirmedIds?: Set<string>;
}
const FIELDS = ['user_id', 'source', 'external_id', 'name', 'exercises', 'started_at', 'completed_at', 'duration_minutes', 'calories_burned'];
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object'
  && !Array.isArray(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const own = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const keys = (value: Record<string, unknown>, expected: string[]): boolean => Object.keys(value).length === expected.length
  && expected.every(key => own(value, key));
const fail = (reason: string): never => { throw new Error('health_workout_import_' + reason); };
const text = (value: unknown): value is string => typeof value === 'string' && Array.from(value).length >= 1 && Array.from(value).length <= 500;
const integer = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= -2147483648 && value <= 2147483647;
function jsonValue(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (!Array.isArray(value) && !object(value) || ancestors.has(value as object)
    || Array.isArray(value) && (Object.keys(value).length !== value.length || Object.keys(value).some((key, index) => key !== String(index)))) return false;
  ancestors.add(value as object);
  const valid = Object.values(value).every(item => jsonValue(item, ancestors));
  ancestors.delete(value as object);
  return valid;
}
function freeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
}
function equalJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((value, index) => equalJson(value, b[index]));
  if (!object(a) || !object(b)) return false;
  const fields = Object.keys(a);
  return fields.length === Object.keys(b).length && fields.every(key => own(b, key) && equalJson(a[key], b[key]));
}
function inputs(input: readonly WorkoutRow[], owner: string, provider: HealthProvider): WorkoutRow[] {
  if (!Array.isArray(input) || input.length > 100 || !jsonValue(input)) return fail('invalid');
  const rows = structuredClone(input) as WorkoutRow[];
  if (!jsonValue(rows)) return fail('invalid');
  const seen = new Set<string>();
  for (const row of rows) {
    if (!object(row) || !keys(row, FIELDS)) return fail('invalid');
    let rowOwner: string;
    try { rowOwner = bodyHealthId(row.user_id); } catch { return fail('invalid'); }
    if (rowOwner !== owner || row.source !== provider
      || !text(row.name) || !text(row.external_id) || !Array.isArray(row.exercises)
      || !validBodyHealthTimestamp(row.started_at) || !validBodyHealthTimestamp(row.completed_at)
      || bodyHealthTimestampCompare(row.completed_at, row.started_at) < 0
      || !integer(row.duration_minutes) || row.duration_minutes < 0 || row.calories_burned !== null && !integer(row.calories_burned)
      || seen.has(row.external_id)) return fail('invalid');
    row.user_id = owner;
    seen.add(row.external_id);
  }
  return freeze(rows);
}
function confirm(
  data: unknown, rows: WorkoutRow[], owner: string, connection: string, provider: HealthProvider, confirmedIds: Set<string>,
): { delta: HealthWorkoutImportResult; ids: Set<string> } {
  if (!object(data) || !keys(data, ['ok', 'version', 'user_id', 'connection_id', 'provider', 'results']) || !jsonValue(data)
    || data.ok !== true || data.version !== 1 || data.user_id !== owner || data.connection_id !== connection || data.provider !== provider
    || !Array.isArray(data.results) || data.results.length !== rows.length) return fail('unconfirmed');
  const expected = new Map(rows.map(row => [row.external_id, row])), ids = new Set<string>();
  const delta = { written: 0, preserved_conflicts: 0 };
  for (const result of data.results) {
    if (!object(result) || !keys(result, ['status', 'external_id', 'reason', 'record']) || typeof result.external_id !== 'string') return fail('unconfirmed');
    const row = expected.get(result.external_id);
    if (!row) return fail('unconfirmed');
    expected.delete(result.external_id);
    if (result.status === 'protected') {
      if (result.reason !== 'edited' && result.reason !== 'deleted' || result.record !== null) return fail('unconfirmed');
      delta.preserved_conflicts++;
    } else if (result.status === 'written') {
      const raw = result.record;
      if (result.reason !== null || !object(raw) || !FIELDS.every(field => own(raw, field)) || !own(raw, 'edited_at') || raw.edited_at !== null) return fail('unconfirmed');
      let id: string;
      try { id = bodyHealthId(raw.id); } catch { return fail('unconfirmed'); }
      if (raw.id !== id || ids.has(id) || confirmedIds.has(id) || raw.user_id !== owner || raw.source !== provider
        || raw.external_id !== row.external_id || raw.name !== row.name || !equalJson(raw.exercises, row.exercises)
        || !validBodyHealthTimestamp(raw.started_at) || bodyHealthTimestampCompare(raw.started_at, row.started_at) !== 0
        || !validBodyHealthTimestamp(raw.completed_at) || bodyHealthTimestampCompare(raw.completed_at, row.completed_at) !== 0
        || raw.duration_minutes !== row.duration_minutes || raw.calories_burned !== row.calories_burned) return fail('unconfirmed');
      ids.add(id); delta.written++;
    } else return fail('unconfirmed');
  }
  if (expected.size !== 0) return fail('unconfirmed');
  return { delta, ids };
}

/** A written result confirms the requested persisted payload, including a
 * matching existing row. It does not attribute an earlier lost acknowledgement.
 * The server is the protection authority; uncertain replies never retry.
 */
export async function importHealthWorkoutRecords(options: HealthWorkoutImportOptions): Promise<HealthWorkoutImportResult> {
  const { client, signal, assertCurrent, onConfirmed } = options;
  const active = () => {
    assertCurrent();
    if (signal.aborted) throw new DOMException('Health workout import aborted', 'AbortError');
  };
  let owner: string, connection: string;
  try { owner = bodyHealthId(options.ownerId); connection = bodyHealthId(options.connectionId); } catch { return fail('invalid'); }
  const provider = options.provider, bearer = options.bearer, confirmedIds = options.confirmedIds ?? new Set<string>();
  if (!(confirmedIds instanceof Set)) return fail('invalid');
  for (const id of confirmedIds) {
    try { if (bodyHealthId(id) !== id) return fail('invalid'); } catch { return fail('invalid'); }
  }
  if (!['apple_health', 'health_connect'].includes(provider) || typeof bearer !== 'string' || !bearer
    || Array.from(bearer).some(character => /\s/.test(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return fail('invalid');
  const rows = inputs(options.rows, owner, provider);
  const args = freeze({ p_connection_id: connection, p_provider: provider, p_records: rows, ...(options.recentRunId ? { p_recent_run_id: bodyHealthId(options.recentRunId) } : {}) });
  active();
  if (rows.length === 0) return { written: 0, preserved_conflicts: 0 };
  if (typeof client.rpc !== 'function') return fail('unavailable');
  let reply: unknown;
  try {
    reply = await (client as HealthWorkoutImportRpcClient).rpc('import_native_health_workouts_v1', args)
      .abortSignal(signal).setHeader('Authorization', 'Bearer ' + bearer);
  } finally { active(); }
  if (!object(reply)) return fail('unconfirmed');
  if (reply.error !== null) throw reply.error ?? new Error('health_workout_import_unconfirmed');
  if (!Number.isInteger(reply.status) || Number(reply.status) < 200 || Number(reply.status) >= 300) return fail('unconfirmed');
  const { delta, ids } = confirm(reply.data, rows, owner, connection, provider, confirmedIds);
  active();
  for (const id of ids) confirmedIds.add(id);
  onConfirmed?.(Object.freeze(delta));
  return delta;
}
